import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { thumbUrl } from '@/features/attachments/api'
import type { FlatNode, TreeNode } from '@/features/trees/api'
import { flatten, subtreeIds } from '@/features/trees/api'
import { cn } from '@/lib/utils'

export interface TreePickerProps {
  nodes: TreeNode[]
  value: string | null
  onChange: (id: string | null) => void
  placeholder?: string
  /** Label of the "no parent" option; omit to make the choice required. */
  noneLabel?: string
  /**
   * Exclude this node and everything beneath it. Used when picking a parent:
   * filing a node inside its own branch is not a legal move.
   */
  excludeSubtreeOf?: string | null
  id?: string
  disabled?: boolean
  /**
   * Accessible name for the trigger. The visible `<Label>` next to a picker
   * cannot be associated with it — the trigger is a button, not a form control —
   * so it needs its own name or screen readers announce only its current value.
   */
  label?: string
}

/** A searchable picker that shows each option's full path. */
export function TreePicker({
  nodes,
  value,
  onChange,
  placeholder = 'Choose…',
  noneLabel,
  excludeSubtreeOf,
  id,
  disabled,
  label,
}: TreePickerProps) {
  const [open, setOpen] = useState(false)

  const options: FlatNode[] = useMemo(() => {
    const all = flatten(nodes)
    if (!excludeSubtreeOf) return all
    const excluded = subtreeIds(all, excludeSubtreeOf)
    return all.filter((node) => !excluded.has(node.id))
  }, [nodes, excludeSubtreeOf])

  const selected = options.find((node) => node.id === value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          disabled={disabled}
          className="bg-surface border-input hover:border-foreground/45 hover:bg-surface w-full min-w-0 shrink justify-between font-sans font-normal"
        >
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {selected ? selected.path : (noneLabel ?? placeholder)}
          </span>
          <ChevronsUpDownIcon className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0">
        <Command>
          <CommandInput placeholder="Search…" />
          <CommandList>
            <CommandEmpty>Nothing matches.</CommandEmpty>
            <CommandGroup>
              {noneLabel && (
                <CommandItem
                  value={noneLabel}
                  onSelect={() => {
                    onChange(null)
                    setOpen(false)
                  }}
                >
                  <CheckIcon className={cn(value === null ? 'opacity-100' : 'opacity-0')} />
                  <span className="text-muted-foreground">{noneLabel}</span>
                </CommandItem>
              )}
              {options.map((node) => (
                <CommandItem
                  key={node.id}
                  value={node.path}
                  onSelect={() => {
                    onChange(node.id)
                    setOpen(false)
                  }}
                >
                  <CheckIcon className={cn(value === node.id ? 'opacity-100' : 'opacity-0')} />
                  {node.previewAttachmentId && (
                    <img
                      src={thumbUrl(node.previewAttachmentId)}
                      alt=""
                      loading="lazy"
                      className="size-5 shrink-0 rounded-sm border object-cover"
                    />
                  )}
                  <span className="truncate">{node.path}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
